import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router';
import { useAuth } from '@clerk/react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../hooks/use-api-client.js';
import { usePreferencesStore } from '../../stores/preferences.js';
import { PageTransition } from '../app/page-transition.js';

type PageState =
  | 'loading'
  | 'sign-in-required'
  | 'input'
  | 'verifying'
  | 'verified'
  | 'approving'
  | 'approved'
  | 'error';

interface DeviceInfo {
  userCode: string;
  clientInfo: { userAgent: string; ip: string };
  expiresAt: string;
}

export function DeviceAuthPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const { t } = useTranslation('common');
  const { fetchApi } = useApiClient();
  const locale = usePreferencesStore((s) => s.locale);
  const [searchParams] = useSearchParams();

  const initialCode = searchParams.get('code') ?? '';
  const [code, setCode] = useState(initialCode);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const [autoVerified, setAutoVerified] = useState(false);

  // Resolve auth state
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setPageState('sign-in-required');
      return;
    }
    setPageState('input');
  }, [isLoaded, isSignedIn]);

  // Auto-format code input: uppercase, auto-dash
  const handleCodeChange = useCallback((value: string) => {
    const cleaned = value
      .toUpperCase()
      .replace(/[^A-HJ-NP-Z]/g, '')
      .slice(0, 8);
    if (cleaned.length > 4) {
      setCode(`${cleaned.slice(0, 4)}-${cleaned.slice(4)}`);
    } else {
      setCode(cleaned);
    }
  }, []);

  const handleVerify = useCallback(async () => {
    const normalized = code.toUpperCase().trim();
    if (!/^[A-HJ-NP-Z]{4}-[A-HJ-NP-Z]{4}$/.test(normalized)) {
      setErrorMessage(t('deviceAuth.validationError'));
      setPageState('error');
      return;
    }

    setPageState('verifying');
    setErrorMessage('');

    try {
      const res = await fetchApi(`/v1/device/verify?user_code=${normalized}`);
      const body = await res.json();

      if (!res.ok) {
        const msg = body?.error?.message ?? t('deviceAuth.notFoundError');
        setErrorMessage(msg);
        setPageState('error');
        return;
      }

      setDeviceInfo(body.data);
      setPageState('verified');
    } catch {
      setErrorMessage(t('deviceAuth.verifyError'));
      setPageState('error');
    }
  }, [code, fetchApi, t]);

  // Auto-verify when code is pre-filled from URL
  useEffect(() => {
    if (pageState === 'input' && initialCode && !autoVerified) {
      setAutoVerified(true);
      handleVerify();
    }
  }, [pageState, initialCode, autoVerified, handleVerify]);

  const handleApprove = useCallback(async () => {
    if (!deviceInfo) return;

    setPageState('approving');
    setErrorMessage('');

    try {
      const res = await fetchApi('/v1/device/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userCode: deviceInfo.userCode }),
      });
      const body = await res.json();

      if (!res.ok) {
        const msg = body?.error?.message ?? t('deviceAuth.approveErrorTitle');
        setErrorMessage(msg);
        setPageState('error');
        return;
      }

      setPageState('approved');
    } catch {
      setErrorMessage(t('deviceAuth.approveError'));
      setPageState('error');
    }
  }, [deviceInfo, fetchApi, t]);

  // Redirect to sign-in
  if (pageState === 'sign-in-required') {
    const redirectUrl = `/device${code ? `?code=${code}` : ''}`;
    window.location.href = `/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`;
    return (
      <PageTransition>
        <div className="flex items-center justify-center py-20" role="status" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-secondary border-t-primary" />
        </div>
      </PageTransition>
    );
  }

  // Loading state
  if (pageState === 'loading') {
    return (
      <PageTransition>
        <div className="flex items-center justify-center py-20" role="status" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-secondary border-t-primary" />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="mx-auto w-full max-w-[640px] px-6 pb-32 pt-16 standard:px-8">
        <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Device auth
        </p>
        <h1 className="mt-4 font-sans text-[clamp(32px,5vw,48px)] font-light leading-[1.05] tracking-[-0.03em] text-primary">
          {pageState === 'approved'
            ? 'Device connected.'
            : pageState === 'verified'
              ? 'Approve this device?'
              : t('deviceAuth.title')}
        </h1>
        <p className="mb-12 mt-4 max-w-[60ch] text-[16px] leading-[1.6] text-secondary">
          {pageState === 'approved'
            ? t('deviceAuth.successDescription')
            : pageState === 'verified'
              ? 'Confirm the connecting client below. Approving issues an API key that lives only in your CLI — you can revoke it anytime from your account.'
              : t('deviceAuth.description')}
        </p>

        {/* Success state — clean editorial card with check icon */}
        {pageState === 'approved' && (
          <div
            className="flex flex-col gap-3 rounded-lg border border-accent-lime/30 bg-surface/40 p-7 text-center"
            role="status"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="mx-auto h-8 w-8 text-accent-lime"
              aria-hidden="true"
            >
              <path
                d="M5 12l5 5L20 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="font-sans text-[20px] font-medium text-primary">
              {t('deviceAuth.successTitle')}
            </p>
          </div>
        )}

        {/* Error message */}
        {pageState === 'error' && errorMessage && (
          <div
            className="mb-6 rounded-md border border-accent-red/30 bg-accent-red/[0.06] px-4 py-3 text-[13px] text-accent-red"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {/* Code input */}
        {(pageState === 'input' || pageState === 'error') && (
          <div className="flex flex-col gap-4 rounded-lg border border-white/[0.06] bg-surface/40 p-7">
            <label
              htmlFor="device-code"
              className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
            >
              {t('deviceAuth.codeLabel')}
            </label>
            <input
              id="device-code"
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder={t('deviceAuth.codePlaceholder')}
              className="w-full rounded-md border border-white/[0.06] bg-canvas px-5 py-4 text-center font-mono text-[28px] font-light tabular-nums tracking-[0.2em] text-primary placeholder:text-muted/40 outline-none transition-colors focus:border-accent-cyan/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              maxLength={9}
              autoFocus
            />
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleVerify}
                disabled={code.length < 9}
                className="inline-flex items-center gap-2 rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                {t('deviceAuth.verify')}
              </button>
            </div>
          </div>
        )}

        {/* Verifying spinner */}
        {pageState === 'verifying' && (
          <div
            className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-surface/40 p-7"
            role="status"
            aria-busy="true"
          >
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-secondary border-t-primary" />
            <span className="font-mono text-[12px] tracking-[0.04em] text-muted">
              {t('deviceAuth.verifying')}
            </span>
          </div>
        )}

        {/* Device details + approve */}
        {pageState === 'verified' && deviceInfo && (
          <div className="flex flex-col gap-6 rounded-lg border border-white/[0.06] bg-surface/40 p-7">
            <p className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
              {t('deviceAuth.detailsTitle')}
            </p>

            <dl className="m-0 flex flex-col gap-4">
              <div className="grid grid-cols-[1fr_2fr] items-baseline gap-4 border-t border-white/[0.06] pt-4 first:border-t-0 first:pt-0 text-[13px]">
                <dt className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
                  {t('deviceAuth.userAgent')}
                </dt>
                <dd className="m-0 break-all text-secondary">{deviceInfo.clientInfo.userAgent}</dd>
              </div>
              <div className="grid grid-cols-[1fr_2fr] items-baseline gap-4 border-t border-white/[0.06] pt-4 text-[13px]">
                <dt className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
                  {t('deviceAuth.ipAddress')}
                </dt>
                <dd className="m-0 font-mono text-secondary">{deviceInfo.clientInfo.ip}</dd>
              </div>
              <div className="grid grid-cols-[1fr_2fr] items-baseline gap-4 border-t border-white/[0.06] pt-4 text-[13px]">
                <dt className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">
                  {t('deviceAuth.expires')}
                </dt>
                <dd className="m-0 font-mono text-secondary">
                  {new Date(deviceInfo.expiresAt).toLocaleTimeString(locale)}
                </dd>
              </div>
            </dl>

            <button
              onClick={handleApprove}
              className="inline-flex items-center justify-center gap-2 self-start rounded-md bg-accent-lime px-4 py-2.5 text-[13px] font-semibold text-canvas transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
            >
              {t('deviceAuth.approve')}
            </button>
          </div>
        )}

        {/* Approving spinner */}
        {pageState === 'approving' && (
          <div
            className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-surface/40 p-7"
            role="status"
            aria-busy="true"
          >
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-secondary border-t-primary" />
            <span className="font-mono text-[12px] tracking-[0.04em] text-muted">
              {t('deviceAuth.approving')}
            </span>
          </div>
        )}
      </div>
    </PageTransition>
  );
}
